/**
 * Modified: `VariantSelectedEvent.promise` settles when that selection's section fetch ends (update or abort).
 * LRU + in-flight dedupe.
 * Click path uses `abortVariantHtmlInflightExcept` when URL changes. Prefetch cancels only previous prefetch URLs
 * and never aborts a protected URL (latest click, or pointerdown intent) while users are switching variants rapidly;
 * pointerleave only clears debounce. Pointerdown starts the same section fetch (high priority, shared inflight)
 * before `change`, and warms the featured image. Cached click apply is synchronous (generation-gated).
 * Section Rendering (`section_id`) on PDP; hover prefetch idle + low priority.
 */
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { normalizeSectionId } from '@theme/section-renderer';
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
  isLowPowerDevice,
  createDeferredPromise,
} from '@theme/utilities';

/** Max cached variant HTML strings; keeps memory bounded (helps mobile / long sessions). */
const VARIANT_RESPONSE_CACHE_MAX = 16;

/** Give up on a hung variant fetch. Generous enough for a slow section render on a poor connection. */
const VARIANT_FETCH_TIMEOUT = 10000;

/** @type {Map<string, string>} */
const variantResponseCache = new Map();

/**
 * @param {string} url
 * @returns {string | undefined}
 */
function variantResponseCacheGet(url) {
  if (!variantResponseCache.has(url)) return undefined;
  const text = /** @type {string} */ (variantResponseCache.get(url));
  variantResponseCache.delete(url);
  variantResponseCache.set(url, text);
  return text;
}

/**
 * @param {string} url
 * @param {string} text
 */
function variantResponseCacheSet(url, text) {
  if (!url || !text) return;
  if (variantResponseCache.has(url)) variantResponseCache.delete(url);
  variantResponseCache.set(url, text);
  while (variantResponseCache.size > VARIANT_RESPONSE_CACHE_MAX) {
    const oldest = variantResponseCache.keys().next().value;
    if (oldest === undefined) break;
    variantResponseCache.delete(oldest);
  }
}

/**
 * @returns {boolean}
 */
function variantResponseCacheDisabled() {
  return typeof Shopify !== 'undefined' && Boolean(Shopify.designMode);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
function variantResponseCacheHas(url) {
  return variantResponseCache.has(url);
}

/**
 * In-flight dedupe: one fetch per URL; multiple callers (prefetch + click) share the same promise.
 *
 * @type {Map<string, { promise: Promise<string>, controller: AbortController }>}
 */
const variantHtmlInflight = new Map();

/**
 * @param {string} url
 */
function abortVariantHtmlInflight(url) {
  const entry = variantHtmlInflight.get(url);
  if (!entry) return;
  entry.controller.abort();
}

/**
 * @param {string} keepUrl
 */
function abortVariantHtmlInflightExcept(keepUrl) {
  for (const u of [...variantHtmlInflight.keys()]) {
    if (u !== keepUrl) abortVariantHtmlInflight(u);
  }
}

/**
 * @param {string} url
 * @param {{ priority?: 'low' | 'high' | 'auto' }} [options]
 * @returns {Promise<string>}
 */
function loadVariantSectionHtml(url, options = {}) {
  const { priority = 'auto' } = options;

  if (variantResponseCacheDisabled()) {
    return fetchVariantSectionText(url, new AbortController(), priority);
  }

  const cached = variantResponseCache.get(url);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }

  const existing = variantHtmlInflight.get(url);
  if (existing) {
    return existing.promise;
  }

  const controller = new AbortController();
  const promise = fetchVariantSectionText(url, controller, priority).finally(() => {
    variantHtmlInflight.delete(url);
  });

  variantHtmlInflight.set(url, { promise, controller });
  return promise;
}

/**
 * Fetch section HTML, giving up on a stalled connection. Aborting via the existing controller (rather
 * than `AbortSignal.timeout`) keeps the rejection an `AbortError`, so a timeout travels the same path
 * as a supersede: the selection promise rejects and every listener clears its busy state at once.
 * @param {string} url
 * @param {AbortController} controller
 * @param {'low' | 'high' | 'auto'} priority
 * @returns {Promise<string>}
 */
function fetchVariantSectionText(url, controller, priority) {
  const timer = setTimeout(() => controller.abort(), VARIANT_FETCH_TIMEOUT);

  /** @type {RequestInit & { priority?: string }} */
  const init = { signal: controller.signal };
  if (priority !== 'auto') {
    init.priority = priority;
  }

  return fetch(url, init)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Variant fetch failed: ${response.status}`);
      }
      return response.text();
    })
    .finally(() => clearTimeout(timer));
}

/**
 * Rejection used when a newer selection supersedes an in-flight one, so listeners can tell
 * "this selection never applied" apart from "it applied with no variant".
 * @returns {DOMException}
 */
function supersededError() {
  return new DOMException('Superseded by a newer variant selection', 'AbortError');
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {number} Monotonic id for the latest variant fetch (superseded requests must not update UI). */
  #fetchGeneration = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {AbortController | undefined} */
  #prefetchListenersAbort;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #prefetchDebounceTimer;

  /** @type {string | undefined} Last URL started for hover prefetch (used to abort superseded prefetch only). */
  #lastPrefetchUrl;

  /** @type {string | undefined} Most recent click/selection request URL (never abort from prefetch supersede). */
  #latestClickRequestUrl;

  /**
   * URL started from pointerdown (same request the following `change` will make). Must not be
   * aborted by hover prefetch. Must not abort an in-flight *click* fetch — the press may be cancelled.
   * @type {string | undefined}
   */
  #pointerIntentUrl;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    this.#prefetchListenersAbort = new AbortController();
    const prefetchSignal = this.#prefetchListenersAbort.signal;
    this.addEventListener('pointerenter', this.#onPrefetchPointerEnter, { capture: true, signal: prefetchSignal });
    this.addEventListener('pointerleave', this.#onPrefetchPointerLeave, { capture: true, signal: prefetchSignal });
    this.addEventListener('pointerdown', this.#onPointerIntent, { capture: true, signal: prefetchSignal, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelPrefetchScheduling();
    this.#prefetchListenersAbort?.abort();
    this.#prefetchListenersAbort = undefined;
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    /** @type {import('@theme/utilities').DeferredPromise<{ detail: Object | null }>} */
    const deferred = createDeferredPromise();
    this.dispatchEvent(
      new VariantSelectedEvent(
        {
          id: selectedOption.dataset.optionValueId ?? '',
        },
        { promise: deferred.promise }
      )
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, deferred);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = this.#buildOptionQueryParams(selectedOption, source, sourceSelectedOptionsValues);
    return this.#composeRequestUrl(productUrl, params);
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string[]}
   */
  #buildOptionQueryParams(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    return params;
  }

  /**
   * @param {string | undefined} productUrl
   * @param {string[]} params
   * @returns {string}
   */
  #composeRequestUrl(productUrl, params) {
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      let base = productUrl;
      if (base?.includes('?')) {
        base = base.split('?')[0];
      }
      return `${base}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    const parentSectionRenderingId = this.dataset.sectionId;
    if (parentSectionRenderingId) {
      let base = productUrl;
      if (base?.includes('?')) {
        base = base.split('?')[0];
      }
      const normalizedId = normalizeSectionId(parentSectionRenderingId);
      const queryParts = [`section_id=${encodeURIComponent(normalizedId)}`];
      if (params.length) queryParts.push(...params);
      return `${base}?${queryParts.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @returns {boolean}
   */
  #isProductVariantPicker() {
    if (this.closest('quick-add-dialog, product-card')) return false;
    if (this.dataset.templateProductMatch === 'true') return true;
    if (this.closest('featured-product-information')) return true;
    return false;
  }

  /**
   * @returns {boolean}
   */
  #saveDataEnabled() {
    const nav = /** @type {Navigator & { connection?: { saveData?: boolean } }} */ (navigator);
    return Boolean(nav.connection?.saveData);
  }

  /**
   * Hover prefetch: skip Save-Data and low-power (speculative extra bytes).
   * @returns {boolean}
   */
  #shouldPrefetchVariantHtml() {
    if (variantResponseCacheDisabled()) return false;
    if (this.#saveDataEnabled()) return false;
    if (isLowPowerDevice()) return false;
    return this.#isProductVariantPicker();
  }

  /**
   * Pointerdown starts the same URL the following `change` will request — not speculative in the
   * hover sense. Skip only customizer (no cache/dedupe) and Save-Data (finger may cancel).
   * Low-power devices still start here; they need the overlap most.
   * @returns {boolean}
   */
  #shouldStartPointerIntentFetch() {
    if (variantResponseCacheDisabled()) return false;
    if (this.#saveDataEnabled()) return false;
    return this.#isProductVariantPicker();
  }

  /**
   * @param {string | undefined} url
   * @returns {boolean}
   */
  #isProtectedVariantUrl(url) {
    if (!url) return false;
    return url === this.#latestClickRequestUrl || url === this.#pointerIntentUrl;
  }

  /**
   * Option value IDs in DOM order (matches `selectedOptionsValues` ordering).
   *
   * @param {Element} hoverTarget
   * @returns {string[] | null}
   */
  #getHypotheticalOptionValueIds(hoverTarget) {
    const form = this.querySelector('form');
    if (!form) return null;

    const groups = form.querySelectorAll(':scope > fieldset, :scope > .variant-option--dropdowns');
    /** @type {string[]} */
    const ids = [];

    for (const group of groups) {
      if (group instanceof HTMLFieldSetElement) {
        const hoverInGroup = hoverTarget.closest('fieldset') === group;
        let input = hoverInGroup ? this.#radioInputFromHoverTarget(hoverTarget, group) : null;
        if (!(input instanceof HTMLInputElement)) {
          input = /** @type {HTMLInputElement | null} */ (group.querySelector('input:checked'));
        }
        const oid = input?.dataset.optionValueId;
        if (!oid) return null;
        ids.push(oid);
      } else if (group.classList.contains('variant-option--dropdowns')) {
        const select = group.querySelector('select');
        if (!(select instanceof HTMLSelectElement)) return null;
        const option =
          hoverTarget.closest('select') === select && hoverTarget instanceof HTMLOptionElement
            ? hoverTarget
            : select.querySelector('option[selected]');
        const oid = option instanceof HTMLOptionElement ? option.dataset.optionValueId : undefined;
        if (!oid) return null;
        ids.push(oid);
      }
    }

    return ids;
  }

  /**
   * @param {Element} hoverTarget
   * @param {HTMLFieldSetElement} fieldset
   * @returns {HTMLInputElement | null}
   */
  #radioInputFromHoverTarget(hoverTarget, fieldset) {
    if (hoverTarget instanceof HTMLInputElement && hoverTarget.type === 'radio' && fieldset.contains(hoverTarget)) {
      return hoverTarget;
    }
    const label = hoverTarget.closest('label');
    if (label && fieldset.contains(label)) {
      const input = label.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) return input;
    }
    return null;
  }

  /**
   * @param {Element} hoverTarget
   * @returns {string | null}
   */
  #buildPrefetchUrlForHover(hoverTarget) {
    if (!hoverTarget.closest('fieldset')) return null;

    const ids = this.#getHypotheticalOptionValueIds(hoverTarget);
    if (!ids?.length) return null;

    const current = this.selectedOptionsValues;
    if (current.length === ids.length && ids.every((id, i) => id === current[i])) return null;

    const anchor = this.#radioInputFromHoverTarget(hoverTarget, /** @type {HTMLFieldSetElement} */ (hoverTarget.closest('fieldset')));
    const connected = anchor?.dataset?.connectedProductUrl;
    const currentPath = this.dataset.productUrl?.split('?')[0];
    if (connected && currentPath && connected !== currentPath) return null;

    const productUrl = connected || this.#pendingRequestUrl || this.dataset.productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${ids.join(',')}`);

    return this.#composeRequestUrl(productUrl, params);
  }

  /** Clears debounced prefetch start only (pointer left swatch); in-flight fetch keeps running. */
  #cancelPrefetchDebounce() {
    if (this.#prefetchDebounceTimer != null) {
      clearTimeout(this.#prefetchDebounceTimer);
      this.#prefetchDebounceTimer = undefined;
    }
  }

  /** Debounce + abort prefetch-target URL when the element disconnects. */
  #cancelPrefetchScheduling() {
    this.#cancelPrefetchDebounce();
    if (this.#lastPrefetchUrl && !this.#isProtectedVariantUrl(this.#lastPrefetchUrl)) {
      abortVariantHtmlInflight(this.#lastPrefetchUrl);
    }
    this.#lastPrefetchUrl = undefined;
  }

  /** @param {PointerEvent} event */
  #onPrefetchPointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.#shouldPrefetchVariantHtml()) return;
    if (!event.target.closest('fieldset')) return;

    this.#cancelPrefetchDebounce();

    const hoverEl = event.target;
    this.#prefetchDebounceTimer = setTimeout(() => {
      this.#prefetchDebounceTimer = undefined;
      const url = this.#buildPrefetchUrlForHover(hoverEl);
      if (!url) return;

      if (this.#lastPrefetchUrl && this.#lastPrefetchUrl !== url && !this.#isProtectedVariantUrl(this.#lastPrefetchUrl)) {
        abortVariantHtmlInflight(this.#lastPrefetchUrl);
      }
      this.#lastPrefetchUrl = url;
      this.#prefetchVariantImageForTarget(hoverEl);

      if (variantResponseCacheHas(url)) return;

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(
          () => {
            if (variantResponseCacheHas(url)) return;
            this.#runVariantHtmlPrefetch(url);
          },
          { timeout: 2500 }
        );
      } else {
        setTimeout(() => {
          if (variantResponseCacheHas(url)) return;
          this.#runVariantHtmlPrefetch(url);
        }, 0);
      }
    }, 140);
  };

  /** @param {PointerEvent} event */
  #onPrefetchPointerLeave = (event) => {
    if (!(event.target instanceof Element)) return;
    const fieldset = event.target.closest('fieldset');
    if (!fieldset) return;
    if (event.relatedTarget instanceof Element && fieldset.contains(event.relatedTarget)) return;

    this.#cancelPrefetchDebounce();
  };

  /**
   * @param {string} url
   * @param {{ priority?: 'low' | 'high' | 'auto' }} [options]
   */
  async #runVariantHtmlPrefetch(url, options = {}) {
    const { priority = 'low' } = options;

    try {
      const text = await loadVariantSectionHtml(url, { priority });
      if (variantResponseCacheHas(url)) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      if (!doc.querySelector('variant-picker script[type="application/json"]')?.textContent) return;
      variantResponseCacheSet(url, text);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
    }
  }

  /**
   * Warm the featured image for a swatch/option without touching the live gallery
   * (a cancelled press must not change what is on screen).
   * @param {Element} target
   */
  #prefetchVariantImageForTarget(target) {
    if (this.#saveDataEnabled()) return;

    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    const input = this.#radioInputFromHoverTarget(target, fieldset);
    if (!(input instanceof HTMLInputElement)) return;

    const mediaId = input.dataset.featuredMediaId;
    const section = this.closest('.shopify-section, dialog');

    if (mediaId && section) {
      const img = section.querySelector(
        `media-gallery [data-media-id="${CSS.escape(mediaId)}"] img.product-media__image`
      );

      if (img instanceof HTMLImageElement) {
        const url = img.currentSrc || img.src;
        if (url) this.#warmImageUrl(url);
        return;
      }
    }

    const src = input.dataset.featuredMediaSrc;
    if (src) this.#warmImageUrl(src);
  }

  /**
   * @param {string} url
   */
  #warmImageUrl(url) {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }

  /**
   * Start the section fetch on press so `change` can share the in-flight promise.
   * Does not abort a committed click fetch — the user may cancel the press.
   * @param {PointerEvent} event
   */
  #onPointerIntent = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!(event.target instanceof Element)) return;
    if (!this.#shouldStartPointerIntentFetch()) return;
    if (!event.target.closest('fieldset')) return;

    const url = this.#buildPrefetchUrlForHover(event.target);
    if (!url) return;

    this.#pointerIntentUrl = url;
    this.#prefetchVariantImageForTarget(event.target);

    if (
      this.#lastPrefetchUrl &&
      this.#lastPrefetchUrl !== url &&
      !this.#isProtectedVariantUrl(this.#lastPrefetchUrl)
    ) {
      abortVariantHtmlInflight(this.#lastPrefetchUrl);
    }

    if (variantResponseCacheHas(url)) return;

    this.#runVariantHtmlPrefetch(url, { priority: 'high' });
  };

  /**
   * @param {boolean} pending
   */
  #setVariantHtmlPending(pending) {
    this.toggleAttribute('data-variant-html-pending', pending);
  }

  /**
   * @param {number} generation
   */
  #clearVariantHtmlPendingIfCurrent(generation) {
    if (generation === this.#fetchGeneration) {
      this.#setVariantHtmlPending(false);
    }
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {import('@theme/utilities').DeferredPromise<{ detail: Object | null }>} [deferred] - Settled when this fetch ends.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, deferred = createDeferredPromise()) {
    this.#latestClickRequestUrl = requestUrl;
    this.#pointerIntentUrl = requestUrl;
    abortVariantHtmlInflightExcept(requestUrl);
    const generation = ++this.#fetchGeneration;
    this.#setVariantHtmlPending(true);

    /**
     * Apply the response, guaranteeing the deferred settles even on an unexpected throw — a listener
     * awaiting it (add-to-cart queue, loading shimmer) would otherwise stay busy forever.
     * @param {string} responseText
     * @param {boolean} fromCache
     */
    const apply = (responseText, fromCache) => {
      if (generation !== this.#fetchGeneration) {
        this.#settleFetchWithoutUpdate(deferred, supersededError(), generation);
        return;
      }

      try {
        this.#applyVariantResponseText(
          responseText,
          generation,
          requestUrl,
          fromCache,
          morphElementSelector,
          deferred
        );
      } catch (error) {
        console.error(error);
        this.#settleFetchWithoutUpdate(deferred, error, generation);
      }
    };

    const useCache = !variantResponseCacheDisabled();
    if (useCache) {
      const cached = variantResponseCacheGet(requestUrl);
      if (cached !== undefined) {
        // Apply immediately. Yielding a frame here made rapid cached switches more likely to
        // paint an intermediate variant; apply() is already generation-gated.
        apply(cached, true);
        return deferred.promise;
      }
    }

    loadVariantSectionHtml(requestUrl, { priority: 'high' })
      .then((responseText) => apply(responseText, useCache && variantResponseCacheHas(requestUrl)))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.error(error);
        this.#settleFetchWithoutUpdate(deferred, error, generation);
      });

    return deferred.promise;
  }

  /**
   * Settle a selection's deferred for a fetch that produced no `variant:update`. Rejecting marks
   * "never applied" (abort/supersede/error) so listeners can re-resolve; resolving with a null
   * detail marks "applied, but no variant".
   * @param {import('@theme/utilities').DeferredPromise<{ detail: Object | null }>} deferred
   * @param {unknown} [error]
   * @param {number} [generation]
   */
  #settleFetchWithoutUpdate(deferred, error, generation) {
    if (error) {
      deferred.reject(error);
    } else {
      deferred.resolve({ detail: null });
    }

    if (typeof generation === 'number') {
      this.#clearVariantHtmlPendingIfCurrent(generation);
    }
  }

  /**
   * Parse morph + dispatch variant:update. Optionally stores response in LRU when from network and successful.
   *
   * @param {string} responseText
   * @param {number} generation
   * @param {string} requestUrl
   * @param {boolean} fromCache
   * @param {string | undefined} morphElementSelector
   * @param {import('@theme/utilities').DeferredPromise<{ detail: Object | null }>} deferred
   */
  #applyVariantResponseText(responseText, generation, requestUrl, fromCache, morphElementSelector, deferred) {
    /** @param {unknown} [error] */
    const settleWithoutUpdate = (error) => this.#settleFetchWithoutUpdate(deferred, error, generation);

    if (generation !== this.#fetchGeneration) {
      settleWithoutUpdate(supersededError());
      return;
    }

    this.#pendingRequestUrl = undefined;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) {
      if (fromCache) variantResponseCache.delete(requestUrl);
      settleWithoutUpdate();
      return;
    }

    let variantJson;
    try {
      variantJson = JSON.parse(textContent);
    } catch (parseError) {
      console.error(parseError);
      if (fromCache) variantResponseCache.delete(requestUrl);
      settleWithoutUpdate(parseError);
      return;
    }

    let newProduct;

    try {
      if (morphElementSelector === 'main') {
        this.updateMain(html);
      } else if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const { overflowList } = this.refs;
        const wasSwatchesExpanded =
          overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

        newProduct = this.updateVariantPicker(html);

        if (wasSwatchesExpanded) {
          const overflowListAfterMorph = overflowList;
          if (overflowListAfterMorph instanceof OverflowList) {
            overflowListAfterMorph.showAll();
          }
        }
      }
    } catch (morphError) {
      console.error(morphError);
      if (fromCache) variantResponseCache.delete(requestUrl);
      settleWithoutUpdate(morphError);
      return;
    }

    // Picker morph copies server attributes and would drop this live-only flag.
    this.#setVariantHtmlPending(true);

    if (this.selectedOptionId) {
      this.dispatchEvent(
        new VariantUpdateEvent(variantJson, this.selectedOptionId, {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
          applyGeneration: generation,
        })
      );

      deferred.resolve({
        detail: {
          html,
          productId: this.dataset.productId ?? '',
          newProduct,
          resource: variantJson,
          applyGeneration: generation,
          sourceId: this.selectedOptionId,
        },
      });

      this.#clearVariantHtmlPendingIfCurrent(generation);

      if (!fromCache && !variantResponseCacheDisabled()) {
        variantResponseCacheSet(requestUrl, responseText);
      }
    } else {
      settleWithoutUpdate();
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
